import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Twemoji from "react-twemoji";

import { capitalize } from "util/util";
import { ScopeType } from "sockapi/scope-types";
import { doesSolutionExist } from "sockapi/scope-specs/solution-content-spec";
import { SockAPITeamCollectibleState } from "sockapi/scope-specs/team-collectibles-spec";

import settings from "settings";
import Globals from "Globals";
import { ShortDate, Duration } from "components/dates";
import {
  TeamDisplayNamePreloaded,
  makeTeamPagePath,
} from "components/TeamDisplayName";
import { PuzzleIcon } from "components/Icon";
import { Link, PuzzleLink } from "components/Link";
import { Spinner, LargeSpinner } from "components/Loading";
import DownloadFailed from "components/DownloadFailed";
import SortableTable from "components/SortableTable";
import { resolveRoute } from "components/urls";
import { HINT_STRENGTH_ICON } from "components/PuzzlePage/HintDisplay";
import { PuzzlePageWrapper } from "pages/PuzzlePage";
import {
  useGlobalScopeState,
  useGlobalScopeStatePosthuntable,
  useScopeStateResolvable,
  useScopeStateResolvablePosthuntable,
  useTeamScopeState,
  useTeamScopeStatePosthuntable,
  useTeamMembersScopeStatePosthuntable,
  useScopeStateArrayResolvable,
  usePuzzleDisplayNames,
  useIsHuntOver,
} from "stores/ScopeStateStore";
import {
  useSockClient,
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import { useNavStore } from "stores/NavStore";
import {
  displayNameOrder,
  addFlushSockAPIMessagesEffect,
  downloadTeamStateAsync,
} from "client-util/util";
import { useUILock } from "client-util/useUILock";
import "./TeamPage.css";
import { PuzzleType } from "sockapi/puzzle-data";

function TeamDownloadStateDisplay(props: { teamId: string }) {
  const { teamId } = props;
  const downloadLock = useUILock();
  const sockClient = useSockClient();

  return (
    <div className="download-state">
      <img src="/static/images/cutscenes/laura.png" />
      <div>
        <p>
          Download your team’s state to continue working on the hunt after the
          hunt has closed!
        </p>
        <button
          onClick={() => {
            downloadLock.acquireAndRun(async (getIsCanceled) => {
              await downloadTeamStateAsync(sockClient, teamId, getIsCanceled);
            });
          }}
          disabled={downloadLock.isHeld}
        >
          Download state
        </button>
      </div>
    </div>
  );
}

function TeamTimedUnlockDisplay(props: { teamId: string }) {
  const { teamId } = props;
  const timedUnlockLock = useUILock();
  const sockClient = useSockClient();

  return (
    <div>
      <p>Click the button below to unlock all rounds. This cannot be undone!</p>
      <button
        onClick={() => {
          timedUnlockLock.acquireAndRun(async (getIsCanceled) => {
            await sockClient.sendScopeStepAndWaitAsync(
              { type: ScopeType.TEAM_TIMED_UNLOCK, teamId },
              { teamId }
            );
          });
        }}
        disabled={timedUnlockLock.isHeld}
      >
        Unlock rounds
      </button>
    </div>
  );
}

function TeamCollectiblesDisplay(props: { teamId: string }) {
  const { teamId } = props;
  const ownTeamId = useTeamId();
  const huntEndTime = useTeamScopeState()?.huntEndTime ?? null;
  const teamCollectiblesScopeStateResult = useScopeStateResolvablePosthuntable(
    ScopeType.TEAM_COLLECTIBLES,
    ownTeamId === null ? null : { teamId },
    { hasPosthunt: teamId !== ownTeamId }
  );
  const [sortedCollectibles, setSortedCollectibles] = useState<
    SockAPITeamCollectibleState[] | null
  >(null);

  const teamCollectiblesScopeState =
    teamCollectiblesScopeStateResult === null
      ? null
      : !teamCollectiblesScopeStateResult.success
        ? null
        : teamCollectiblesScopeStateResult.state;
  useEffect(() => {
    if (teamCollectiblesScopeState === null) return;
    if (ownTeamId === null) return;
    if (huntEndTime === null) return;
    setSortedCollectibles(
      Object.values(teamCollectiblesScopeState.collectibles)
        .filter(
          (collectible) =>
            ownTeamId === teamId || collectible.timestamp < huntEndTime
        )
        .sort((collectible1, collectible2) => {
          // Sort larger quantities first.
          const num1 = collectible1.num;
          const num2 = collectible2.num;
          const diffNum = num2 - num1;
          if (diffNum !== 0) return diffNum;

          const name1 = collectible1.name;
          const name2 = collectible2.name;
          return name1.localeCompare(name2);
        })
    );
  }, [ownTeamId, teamId, teamCollectiblesScopeState, huntEndTime]);

  if (teamCollectiblesScopeStateResult === null) return <Spinner inline />;
  if (!teamCollectiblesScopeStateResult.success)
    return <DownloadFailed {...teamCollectiblesScopeStateResult} />;
  if (sortedCollectibles === null) return <Spinner inline={true} />;
  if (sortedCollectibles.length === 0) return null;

  return (
    <div>
      <h3>Artifacts</h3>
      <div className="team-collectibles">
        {sortedCollectibles.map(({ name, icon, num }) => (
          <div key={name} className="team-collectible">
            <Twemoji
              className="team-collectible-icon"
              options={{ folder: "svg", ext: ".svg" }}
            >
              {icon}
            </Twemoji>
            <div className="team-collectible-desc">
              {num === 1 ? "" : `${num} `}
              {capitalize(name)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HintsList(props: { teamId: string }) {
  const { teamId } = props;
  const teamScopeState = useTeamScopeState(teamId);
  const teamHintsScopeState = useScopeStateResolvable(ScopeType.TEAM_HINTS, {
    teamId,
  });
  const hintIds = teamHintsScopeState?.hints
    ? Object.values(teamHintsScopeState.hints)
        .map((hints) => Object.keys(hints))
        .flat()
        .map((hintId) => ({ hintId }))
    : null;
  const hints = useScopeStateArrayResolvable(ScopeType.HINT, hintIds);
  const displayNames = usePuzzleDisplayNames();

  if (
    teamScopeState === null ||
    hintIds === null ||
    hints === null ||
    displayNames === null
  )
    return <Spinner inline={true} />;
  const { puzzles } = teamScopeState;

  const sortedHints = hints
    .filter(({ puzName }) => puzzles[puzName] !== undefined)
    .map(({ puzName, request, response }, i) => ({
      ...puzzles[puzName],
      ...hintIds[i],
      name: displayNames[puzName] ?? "???",
      reqTime: request.timestamp,
      resTime: response?.timestamp ?? Infinity,
      strength: request.strength,
      status: response?.type ?? "pending",
    }))
    .sort((a, b) => b.reqTime - a.reqTime);

  return (
    <SortableTable
      spec={{
        columns: [
          {
            label: "Hints",
            sortFunc: (a, b) => displayNameOrder(a.name, b.name),
            width: "50%",
            isTitle: true,
          },
          {
            label: "Request time",
            sortFunc: (a, b) => a.reqTime - b.reqTime,
            width: "20%",
          },
          {
            label: "Status",
            sortFunc: (a, b) => a.status.localeCompare(b.status),
            width: "10%",
          },
          {
            label: "Response time",
            sortFunc: (a, b) => a.resTime - b.resTime,
            width: "20%",
          },
        ],
        renderRow: (puzData, i) => {
          const { puzName, name, reqTime, resTime, strength, status } = puzData;
          return (
            <tr key={i}>
              <td>
                <PuzzleIcon puzData={puzData} />{" "}
                <PuzzleLink puzName={puzName} subpage="hints" title={name}>
                  {name}
                </PuzzleLink>
              </td>
              <td>
                <ShortDate timestamp={reqTime} />
              </td>
              <td>
                <Twemoji tag="span">{HINT_STRENGTH_ICON[strength]}</Twemoji>{" "}
                {status}
              </td>
              <td>
                {isFinite(resTime) ? <ShortDate timestamp={resTime} /> : "—"}
              </td>
            </tr>
          );
        },
        pageSize: 20,
      }}
      records={sortedHints}
      className="list-table team-solves-table"
    />
  );
}

function SolvesList(props: { teamId: string }) {
  const { teamId } = props;
  const ownTeamId = useTeamId();
  const huntEndTime = useTeamScopeState()?.huntEndTime ?? null;
  const teamScopeStateResult = useTeamScopeStatePosthuntable(
    ownTeamId === null ? null : teamId,
    { hasPosthunt: teamId !== ownTeamId }
  );
  const displayNames = usePuzzleDisplayNames();
  const shouldDisplayAdmin = useShouldDisplayAdmin();

  if (
    teamScopeStateResult === null ||
    displayNames === null ||
    ownTeamId === null ||
    huntEndTime === null
  )
    return <Spinner inline={true} />;
  if (!teamScopeStateResult.success)
    return <DownloadFailed {...teamScopeStateResult} />;
  const { puzzles } = teamScopeStateResult.state;

  const sortedSolves = Object.entries(puzzles)
    .filter(
      ([puzName, { type, solveTime }]) =>
        type !== PuzzleType.STORY &&
        solveTime !== undefined &&
        (ownTeamId === teamId || solveTime < huntEndTime)
    )
    .map(([puzName, puzData]) => ({
      ...puzData,
      puzName,
      solveTime: puzData.solveTime ?? 0,
      name: displayNames[puzName] ?? "???",
    }))
    .sort((a, b) => b.solveTime - a.solveTime);

  return (
    <>
      {shouldDisplayAdmin ? (
        <div>
          <h4>Open puzzles (admin only)</h4>
          {Object.values(puzzles)
            .sort((a, b) => b.unlockTime - a.unlockTime)
            .map((puzData) => {
              const { puzName, unlockTime, solveTime } = puzData;
              if (solveTime !== undefined) return null;
              const name = displayNames[puzName] ?? "???";
              return (
                <div key={puzName}>
                  <PuzzleIcon puzData={puzData} />{" "}
                  <PuzzleLink puzName={puzName}>{name}</PuzzleLink>{" "}
                  <ShortDate timestamp={unlockTime} />
                </div>
              );
            })}
        </div>
      ) : null}
      <SortableTable
        spec={{
          columns: [
            {
              label: "Solves",
              sortFunc: (a, b) => displayNameOrder(a.name, b.name),
              width: "50%",
              isTitle: true,
            },
            {
              label: "Unlock time",
              sortFunc: (a, b) => a.unlockTime - b.unlockTime,
              width: "20%",
            },
            {
              label: "→",
              sortFunc: (a, b) =>
                a.solveTime - a.unlockTime - (b.solveTime - b.unlockTime),
              width: "10%",
            },
            {
              label: "Solve time",
              sortFunc: (a, b) => a.solveTime - b.solveTime,
              width: "20%",
            },
          ],
          renderRow: (puzData, i) => {
            const { puzName, name, isMeta, unlockTime, solveTime } = puzData;
            const subpage = doesSolutionExist(puzData) ? "solution" : "puzzle";
            return (
              <tr key={i} style={isMeta ? { fontWeight: "bold" } : {}}>
                <td>
                  <PuzzleIcon puzData={puzData} />{" "}
                  <PuzzleLink puzName={puzName} subpage={subpage} title={name}>
                    {name}
                  </PuzzleLink>
                </td>
                <td>
                  <ShortDate timestamp={unlockTime} />
                </td>
                <td>
                  <Duration from={unlockTime} to={solveTime} />
                </td>
                <td>
                  <ShortDate timestamp={solveTime} />
                </td>
              </tr>
            );
          },
          pageSize: 20,
        }}
        records={sortedSolves}
        className="list-table team-solves-table"
      />
    </>
  );
}

function TeamPageBody() {
  const teams = useGlobalScopeState(ScopeType.TEAM_LIST);
  const posthuntTeamsResult = useGlobalScopeStatePosthuntable(
    ScopeType.TEAM_LIST,
    true,
    { hasPosthunt: true }
  );
  const posthuntTeams = !posthuntTeamsResult?.success
    ? null
    : posthuntTeamsResult.state;
  const { teamName: teamNameEncoded } = useParams();
  const resolvedTeam =
    posthuntTeams === null || teams === null || teamNameEncoded === undefined
      ? null
      : (Object.values(posthuntTeams).find(
          (team) => team.displayName == decodeURIComponent(teamNameEncoded)
        ) ??
        Object.values(teams).find(
          (team) => team.displayName == decodeURIComponent(teamNameEncoded)
        ) ??
        null);

  const teamId = resolvedTeam?.teamId ?? null;
  const team =
    teamId === null
      ? null
      : (teams?.[teamId] ?? posthuntTeams?.[teamId] ?? null);
  const ownTeamId = useTeamId();
  const teamMembersScopeStateResult = useTeamMembersScopeStatePosthuntable(
    ownTeamId === null ? null : teamId,
    { hasPosthunt: teamId !== ownTeamId }
  );
  const areTeamsLoaded = posthuntTeams !== null && teams !== null;
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const isSameTeamOrAdmin =
    shouldDisplayAdmin ||
    (teamId !== null && ownTeamId !== null && ownTeamId === teamId);
  const isHuntOver = useIsHuntOver();
  const sockClient = useSockClient();

  const unlockedPuzzles = useTeamScopeState()?.puzzles;
  const shouldShowTimedUnlockButton =
    isSameTeamOrAdmin &&
    unlockedPuzzles != null &&
    (!unlockedPuzzles["site-intro"] ||
      !unlockedPuzzles["crosswords-intro"] ||
      !unlockedPuzzles["crater-intro"] ||
      !unlockedPuzzles["atoll-intro"] ||
      !unlockedPuzzles["desert-intro"]);

  if (teamNameEncoded === undefined) throw new Error("expect team name in url");

  useEffect(() => {
    if (!areTeamsLoaded) return;
    if (team !== null) {
      const canonicalPath = makeTeamPagePath(team.displayName);
      if (location.pathname !== resolveRoute(canonicalPath))
        setRedirectRoute({ ...canonicalPath, replace: true });
      return;
    }

    return addFlushSockAPIMessagesEffect(sockClient, () => {
      Globals.toastr.error(
        `Team “${decodeURIComponent(teamNameEncoded)}” not found.`
      );
      setRedirectRoute({ route: "teams" });
    });
  }, [areTeamsLoaded, team, teamNameEncoded, setRedirectRoute, sockClient]);

  if (posthuntTeamsResult !== null && !posthuntTeamsResult.success)
    return <DownloadFailed {...posthuntTeamsResult} />;
  if (!areTeamsLoaded || team === null || teamId === null)
    return <LargeSpinner />;

  return (
    <div className="centered-page team-page">
      <title>{team.displayName}</title>
      <h1>
        <TeamDisplayNamePreloaded teamData={team} noLink={true} />
        {!settings.isPosthunt && isSameTeamOrAdmin ? (
          <div className="button-squish-zone">
            <Link route="edit_team" button={true}>
              Edit team members
            </Link>
            <Link route="password_change" button={true}>
              Change password
            </Link>
          </div>
        ) : null}
      </h1>
      {teamMembersScopeStateResult === null ? (
        <Spinner inline={true} />
      ) : !teamMembersScopeStateResult.success ? (
        <DownloadFailed {...teamMembersScopeStateResult} />
      ) : (
        teamMembersScopeStateResult.state.members?.join(", ")
      )}
      {shouldDisplayAdmin || (isHuntOver && isSameTeamOrAdmin) ? (
        <TeamDownloadStateDisplay teamId={teamId} />
      ) : null}
      {shouldShowTimedUnlockButton ? (
        <TeamTimedUnlockDisplay teamId={teamId} />
      ) : null}
      <TeamCollectiblesDisplay teamId={teamId} />
      {isSameTeamOrAdmin ? <HintsList teamId={teamId} /> : null}
      {isSameTeamOrAdmin || isHuntOver ? <SolvesList teamId={teamId} /> : null}
    </div>
  );
}

function TeamPage() {
  return (
    <PuzzlePageWrapper>
      <TeamPageBody />
    </PuzzlePageWrapper>
  );
}

export default TeamPage;
